import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Globe, User } from "lucide-react";
import Logo from "./Logo";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";

export default function Header() {
  const { t, lang, toggleLang } = useI18n();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const navItems = [
    { to: "/magazine", label: t("nav.magazine"), testid: "nav-magazine" },
    { to: "/articles", label: t("nav.articles"), testid: "nav-articles" },
    { to: "/edito", label: lang === "fr" ? "Édito" : "Editorial", testid: "nav-edito" },
    { to: "/fondatrice", label: t("nav.founder"), testid: "nav-founder" },
  ];

  return (
    <header className="gai-header">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10 flex items-center justify-between h-[104px]">
        <Link to="/" aria-label="Accueil GAI" data-testid="nav-home" className="flex items-center">
          <Logo size={68} className="hidden md:block" />
          <Logo size={52} className="md:hidden" />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                `text-[0.72rem] tracking-[0.22em] uppercase font-semibold transition-colors relative pb-1 ${
                  isActive
                    ? "text-[var(--gai-red)]"
                    : "text-[var(--gai-black)] hover:text-[var(--gai-red)]"
                }`
              }
              style={({ isActive }) => (isActive ? { borderBottom: "2px solid var(--gai-red)" } : {})}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggleLang}
            data-testid="lang-toggle"
            className="gai-btn-ghost hidden sm:flex"
            aria-label="Switch language"
          >
            <Globe size={13} />
            <span>{lang === "fr" ? "FR · EN" : "EN · FR"}</span>
          </button>

          {user && user !== false ? (
            <button
              onClick={() => navigate(user.role === "admin" ? "/admin" : "/dashboard")}
              data-testid={user.role === "admin" ? "nav-admin" : "nav-dashboard"}
              className="gai-btn-outline hidden md:inline-flex"
            >
              <User size={13} />
              {user.role === "admin" ? t("nav.admin") : t("nav.dashboard")}
            </button>
          ) : (
            <button
              onClick={() => navigate("/connexion")}
              data-testid="nav-login"
              className="gai-btn-outline hidden md:inline-flex"
            >
              <User size={13} />
              {t("nav.client")}
            </button>
          )}

          <button
            onClick={() => navigate("/annoncer")}
            data-testid="nav-cta-advertise"
            className="gai-btn-gold hidden md:inline-flex"
          >
            {lang === "fr" ? "Annoncer dans GAI" : "Advertise in GAI"}
          </button>

          <button
            className="lg:hidden p-2 text-[var(--gai-black)]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Open menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[var(--gai-grey-200)] bg-[var(--gai-white)]">
          <div className="px-6 py-6 flex flex-col gap-4">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-${n.testid}`}
                className="text-[0.75rem] tracking-[0.22em] uppercase font-semibold text-[var(--gai-black)]"
              >
                {n.label}
              </NavLink>
            ))}
            <button onClick={toggleLang} className="gai-btn-ghost w-fit" data-testid="mobile-lang-toggle">
              <Globe size={13} /> {lang === "fr" ? "FR · EN" : "EN · FR"}
            </button>
            <button
              onClick={() => { setOpen(false); navigate(user && user !== false ? "/dashboard" : "/connexion"); }}
              className="gai-btn-outline w-fit"
              data-testid="mobile-nav-client"
            >
              {user && user !== false ? t("nav.dashboard") : t("nav.client")}
            </button>
            <button onClick={() => { setOpen(false); navigate("/annoncer"); }} className="gai-btn-gold w-fit" data-testid="mobile-cta-advertise">
              {lang === "fr" ? "Annoncer" : "Advertise"}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
